import * as React from 'react';
import { useEffect, useReducer, useState } from 'react';
import { connect } from 'react-redux';
import { ButtonGroup, Button, Card, Row, Col, UncontrolledPopover } from 'reactstrap';
import { ImpactPartner } from '../../models/ImpactPartner';
import GenericDropdownAsync from '../../components/controls/GenericDropdownAsync';
import ImpactPartnerService from '../../services/ImpactPartnerService';
import ProductInventoryService, { ProductInventorySearchFilter } from '../../services/ProductInventoryService';
import ProductFamilyTreeDropdown from './ProductFamilyTreeDropdown';
import AutocompleteControlAsync from '../../components/controls/AutocompleteAsync';
import { Result } from '../../services/ServiceBase';
import { ApplicationState } from '../../store';
import * as AuthorizationStore from '../../store/Authorization';
import { ProductFamilyTreeSelectedItemInfo } from '../../components/editors/ProductFamilyTree';
import * as Analytics from '../../AnalyticsHelper';
import { nanoid } from 'nanoid';
import qs from 'query-string';

type ProductSearchState = {
    searchBy: 'ProductCode' | 'ProductNumber' | 'ProductLine' | 'Keywords';
    searchFilter: ProductInventorySearchFilter;
    selectedImpactPartner: ImpactPartner;
    productShortCode: string;
    productNumber: string;
    productAssemblyId: string;
    productAssemblyTitle: string;
    keywords: string;
    searchOnChange: boolean;
    productFamilyTreeSelectedProduct: ProductFamilyTreeSelectedItemInfo;
};

const initialState: ProductSearchState = {
    searchBy: "ProductNumber",
    searchFilter: undefined,
    selectedImpactPartner: undefined,
    productShortCode: '',
    productNumber: '',
    productAssemblyId: '',
    productAssemblyTitle: '',
    keywords: '',
    searchOnChange: false,
    productFamilyTreeSelectedProduct: undefined
}

const tooltipProductNumber = "Search for Manufacturer PN such as Fisher Product Number, Isolation Model Code, etc. as well as Impact Partner Part Number.";
const tooltipProductShortCode = "Search for products with Shortcodes based on Product Attributes.  Start your search with the Model.  Currently limited to items with Product Numbers from the Fisher First 2.0 Database.";
const tooltipProductLine = "Select your model from the Product Line Tree to start your search based on Attributes.  Currently limited to items with Product Numbers from the Fisher First 2.0 Database.";
const tooltipKeywords = 'Keyword Search the Descriptions and Brands.  Use of * as a wildcard before or after text and use "text here" for exact match.';

function ProductSearch(props: {
    onSearchFilterChanged?: (sf: ProductInventorySearchFilter) => void;
    authorization: AuthorizationStore.IState;
}) {

    const [state, setState] = useReducer(
        (prevState: ProductSearchState, newState: Partial<ProductSearchState>) => ({ ...prevState, ...newState }),
        initialState
    );

    const [idProdNumberSearchInfo] = useState('id' + nanoid());
    const [idProdShortCodeSearchInfo] = useState('id' + nanoid());
    const [idProdLineSearchInfo] = useState('id' + nanoid());
    const [idKeywordsSearchInfo] = useState('id' + nanoid());

    const parsedUrl = qs.parseUrl(window.location.href);

    const search = () => {

        if (!props.authorization.currentUser)
            return;

        const sf: ProductInventorySearchFilter = {
            impactPartnerId: state.selectedImpactPartner ? state.selectedImpactPartner.Id : undefined,
            productShortCode: state.searchBy === 'ProductCode' ? state.productShortCode : undefined,
            productNumber: state.searchBy === 'ProductNumber' ? state.productNumber : undefined,
            productAssembly: state.searchBy === 'ProductLine' ? state.productAssemblyId : undefined,
            detailsFilters: [],
            keywords: state.searchBy === 'Keywords' ? state.keywords : undefined,
            userImpactPartnerId: props.authorization.currentUser.ImapctParnerId,
            friendlySearchString: undefined
        }

        // calculate  friendlySearchString
        const friendlySearchString: string[] = [];

        // analytics
        let payload = {
            'event': 'inet-search',
            'keywords': '',
            'impact-partner': ''
        };

        if (state.selectedImpactPartner) {
            friendlySearchString.push(`Impact Partner: ${state.selectedImpactPartner.Name}`);
            payload["impact-partner"] = state.selectedImpactPartner.Name;
        }

        if (state.searchBy === 'ProductCode' && (state.productShortCode || '').length > 0) {
            friendlySearchString.push(`Product Code: " ${state.productShortCode}`);
            payload.keywords = state.productShortCode;
        }

        if (state.searchBy === 'ProductNumber' && (state.productNumber || '').length > 0) {
            friendlySearchString.push(`Product Number: " ${state.productNumber}`);
            payload.keywords = state.productNumber;
        }

        if (state.searchBy === 'ProductLine' && (state.productAssemblyTitle || '').length > 0) {
            friendlySearchString.push(`Product Line: " ${state.productAssemblyTitle}`);
            payload.keywords = state.productAssemblyTitle;
        }

        if (state.searchBy === 'Keywords' && (state.keywords || '').length > 0) {
            friendlySearchString.push(`Keywords: " ${state.keywords}`);
            payload.keywords = state.keywords;
        }

        sf.friendlySearchString = friendlySearchString.join(', ');

        if ((payload.keywords || '').length > 0)
            Analytics.push(payload);
        
        // update state
        setState({
            searchFilter: sf
        });

    }

    // clear all existing filters
    const clearFilters = () => {

        setState({
            keywords: '',
            productAssemblyId: '',
            productAssemblyTitle: '',
            productNumber: '',
            productShortCode: '',
            selectedImpactPartner: undefined,
            productFamilyTreeSelectedProduct: undefined,
            searchFilter: {
                friendlySearchString: undefined,
                impactPartnerId: undefined,
                keywords: undefined,
                productAssembly: undefined,
                productNumber: undefined,
                productShortCode: undefined,
                detailsFilters: [],
                userImpactPartnerId: props.authorization.currentUser.ImapctParnerId
            }
        })

        const sf: ProductInventorySearchFilter = {
            impactPartnerId: state.selectedImpactPartner ? state.selectedImpactPartner.Id : undefined,
            productShortCode: state.searchBy === 'ProductCode' ? state.productShortCode : undefined,
            productNumber: state.searchBy === 'ProductNumber' ? state.productNumber : undefined,
            productAssembly: state.searchBy === 'ProductLine' ? state.productAssemblyId : undefined,
            detailsFilters: [],
            keywords: state.searchBy === 'Keywords' ? state.keywords : undefined,
            userImpactPartnerId: props.authorization.currentUser.ImapctParnerId,
            friendlySearchString: undefined
        }
    }

    // on serach filter change
    useEffect(() => {
        if (props.onSearchFilterChanged) {
            props.onSearchFilterChanged(state.searchFilter);
        }
    }, [state.searchFilter]);

    // on component mount
    useEffect(() => {
        let query = parsedUrl.query;
        if (query["pn"]) {
            setState({ searchBy: "ProductNumber", productNumber: query["pn"].toString() });
        }

        search();
    }, [props.authorization.currentUser]);

    return <React.Fragment>
        <ButtonGroup>
            <Button active={state.searchBy === "ProductNumber"} color="light" onClick={() => { setState({ searchBy: "ProductNumber" }) }} className="border px-3 btn-lg">
                <i className="fa fa-barcode mr-1"></i>Product Number
            </Button>
            <Button active={state.searchBy === "ProductCode"} color="light" onClick={() => { setState({ searchBy: "ProductCode" }) }} className="border px-3 btn-lg">
                <i className="fa fa-barcode mr-1"></i>Product Shortcode
            </Button>
            <Button active={state.searchBy === "ProductLine"} color="light" onClick={() => { setState({ searchBy: "ProductLine" }) }} className="border px-3 btn-lg">
                <i className="fa fa-sitemap mr-1"></i>Product Line
            </Button>
            <Button active={state.searchBy === "Keywords"} color="light" onClick={() => { setState({ searchBy: "Keywords" }) }} className="border px-3 btn-lg">
                <i className="fa fa-search mr-1"></i>Keywords
            </Button>
        </ButtonGroup>

        <Card body className="mt-3 pb-2">
            <Row>
                <Col md={5} className="pr-md-0">


                    {/*ProductNumber*/}
                    {state.searchBy === "ProductNumber" && <div className="form-group">
                        <label>Product Number</label><i id={idProdNumberSearchInfo} className="ml-1 fa fa-info-circle cursor-pointer" title={tooltipProductNumber}></i>
                        <AutocompleteControlAsync<string>
                            loadById={(id) => Promise.resolve(Result.FromValue<string>(id || ''))}
                            getId={x => x}
                            searchByKeywordPaged={(keyword, offset) => ProductInventoryService.searchProductNumbers(keyword, 5, offset)}
                            getStringRepresentation={x => x || ''}
                            renderItem={x => x ? x : ''}
                            renderSelectedItem={x => x ? x : ''}
                            allowNonExistingSuggestion={true}
                            allowNullSelection={true}
                            createModelBasedOnId={x => x}
                            onSelectedItemChanged={x => {

                                if (state.productNumber != x) {
                                    setState({ productNumber: x })
                                }

                                if (state.searchOnChange) {
                                    setState({ searchOnChange: false });
                                    search();
                                }
                            }}
                            onEnterKeyPressed={() => {

                                setState({ searchOnChange: true });

                            }}
                            selectedValue={state.productNumber}
                            className="form-control"
                        />

                        <UncontrolledPopover placement="bottom" trigger="legacy focus" target={idProdNumberSearchInfo} className="popover-stock-info popover-bg-black">
                            <div className="text-left p-2">
                                <div className="partner-name">{tooltipProductNumber}</div>
                            </div>
                        </UncontrolledPopover>

                    </div>}

                    {/*ProductCode*/}
                    {state.searchBy === "ProductCode" && <div className="form-group">
                        <label>Product Shortcode</label><i id={idProdShortCodeSearchInfo} className="ml-1 fa fa-info-circle cursor-pointer" title={tooltipProductShortCode}></i>
                        <AutocompleteControlAsync<string>
                            loadById={(id) => Promise.resolve(Result.FromValue<string>(id || ''))}
                            getId={x => x}
                            searchByKeywordPaged={(keyword, offset) => ProductInventoryService.searchProductShortCodes(keyword, 5, offset)}
                            getStringRepresentation={x => x || ''}
                            renderItem={x => x ? x : ''}
                            renderSelectedItem={x => x ? x : ''}
                            allowNonExistingSuggestion={true}
                            allowNullSelection={true}
                            createModelBasedOnId={x => x}
                            onSelectedItemChanged={x => {

                                if (state.productShortCode != x)
                                    setState({ productShortCode: x });

                                if (state.searchOnChange) {
                                    setState({ searchOnChange: false });
                                    search();
                                }

                            }}
                            onEnterKeyPressed={() => {
                                setState({ searchOnChange: true });
                            }}
                            selectedValue={state.productShortCode}
                            className="form-control"
                        />
                        <UncontrolledPopover placement="bottom" trigger="legacy focus" target={idProdShortCodeSearchInfo} className="popover-stock-info popover-bg-black">
                            <div className="text-left p-2">
                                <div className="partner-name">{tooltipProductShortCode}</div>
                            </div>
                        </UncontrolledPopover>
                    </div>}

                    {/*ProductLine*/}
                    {<div className="form-group" style={{ display: (state.searchBy === "ProductLine" ? 'block' : 'none') }}>
                        <label>Product Line</label><i id={idProdLineSearchInfo} className="ml-1 fa fa-info-circle cursor-pointer" title={tooltipProductLine}></i>
                        <ProductFamilyTreeDropdown
                            selectedProductInfo={state.productFamilyTreeSelectedProduct}
                            onProductInfoSelected={prodInfo => setState({ productFamilyTreeSelectedProduct: prodInfo })}
                            onProductSelected={data => {
                                setState({
                                    productAssemblyId: data ? data.EmProductId : undefined,
                                    productAssemblyTitle: data ? data.Description : ''
                                })
                            }} />
                        <UncontrolledPopover placement="bottom" trigger="legacy focus" target={idProdLineSearchInfo} className="popover-stock-info popover-bg-black">
                            <div className="text-left p-2">
                                <div className="partner-name">{tooltipProductLine}</div>
                            </div>
                        </UncontrolledPopover>
                    </div>}

                    {/*Keywords*/}
                    {state.searchBy === "Keywords" && <div className="form-group">
                        <label>Keywords</label><i id={idKeywordsSearchInfo} className="ml-1 fa fa-info-circle cursor-pointer" title={tooltipKeywords}></i>
                        <input type="text" className="form-control"
                            value={state.keywords}
                            onChange={(e) => { setState({ keywords: e.target.value }) }}
                            onKeyDown={(e) => {
                                if (e.key == 'Enter') {
                                    search();
                                }
                            }}
                        />
                        <UncontrolledPopover placement="bottom" trigger="legacy focus" target={idKeywordsSearchInfo} className="popover-stock-info popover-bg-black">
                            <div className="text-left p-2">
                                <div className="partner-name">{tooltipKeywords}</div>
                            </div>
                        </UncontrolledPopover>
                    </div>}

                </Col>
                <Col md={3} className="pr-md-0">
                    <div className="form-group">
                        <label>Impact Partner</label>
                        <GenericDropdownAsync
                            loadDataSource={() => ImpactPartnerService.list()}
                            getId={x => x.Id}
                            renderItem={x => x.Name}
                            renderSelectedItem={x => x ? x.Name : 'Select Impact Partner'}
                            allowNullSelection={true}
                            nullSelectionText='Select Impact Partner'
                            selectedValue={state.selectedImpactPartner ? state.selectedImpactPartner.Id : undefined}
                            onSelectedItemChanged={item => setState({ selectedImpactPartner: item })}
                        />

                    </div>
                </Col>
                <Col md={4}>
                    <div className="d-flex">
                        <div className="form-group">
                            <label className="d-md-block d-sm-none">Search</label>
                            <Button color="primary" className="rounded-0 px-5 text-uppercase" onClick={() => {
                                search()
                            }}>Search</Button>
                        </div>
                        <div className="form-group">
                            <label className="d-md-block d-sm-none">&nbsp;</label>
                            <Button color="clear" onClick={() => {
                                clearFilters()
                            }}>Clear Search</Button>
                        </div>
                    </div>
                </Col>
            </Row>
        </Card>
    </React.Fragment>

}

export default connect((state: ApplicationState) => ({
    authorization: state.authorization
}))(ProductSearch);
